<template>
  <div class="purchase-page-container">
    <payment-type-modal
      v-if="isPaymentTypeModalOpened"
      :order-total-price="Number(totalPrice) + getFixedFeePerTicketAmount"
      @submit="handlePurchase"
      @closeModal="closeModal"
    />
    <successful-payment-modal
      v-if="isSuccessfulPaymentModalOpen"
      @viewOrder="onViewOrderClick"
      @printTickets="onPrintTicketsClick"
      @closeModal="onSuccessfulPaymentModalClose"
    />
    <div class="back-button-container" @click="goTo(routes.ticketSeller.tickets)">
      <div class="back-button-icon-container">
        <img class="back-button-icon" src="/assets/back-icon.svg" alt="Back Button" />
      </div>
      <div class="back-button-text-container">Back to events</div>
    </div>
    <div class="main-container">
      <div>
        <div class="event-container" :style="!event.venueId && 'padding-bottom: 2rem'">
          <div
            class="event-details-container"
            :style="!event.venueId && 'border-bottom: 1px solid #979797;'"
          >
            <div class="event-details-title">
              {{ title }}
            </div>
            <div class="event-details-date-time-location">
              {{ formatDate(dateAndTime, 'MMM') }} {{ formatDate(dateAndTime, 'DD') }},
              {{ formatDate(dateAndTime, 'YYYY') }} at {{ formatDate(dateAndTime, 'HH') }}:{{
                formatDate(dateAndTime, 'mm')
              }}
              @
              {{ location.venue }}
            </div>
            <div class="public-page-link-container" @click="goToPublicPage">
              <div class="public-page-link">View event page</div>
              <div class="public-page-link-icon-container">
                <img class="public-page-link-icon" src="/assets/goto-icon.svg" alt="Go to button" />
              </div>
            </div>
          </div>
          <div v-if="!event.venueId">
            <div v-for="orderItem of orderItems" :key="orderItem.id" class="ticket-container">
              <div class="ticket-details-container">
                <div class="ticket-info-container">
                  <div class="ticket-title">
                    {{ orderItem.name }}
                  </div>
                  <div v-if="orderItem.description" class="ticket-description">
                    {{ orderItem.description }}
                  </div>
                </div>
                <div class="ticket-price-container">
                  <div class="ticket-price">
                    {{ parseFloat(orderItem.price).toFixed(2) }}&nbsp;{{ currency }}
                  </div>
                </div>
              </div>
              <div class="ticket-purchase-container">
                <div class="ticket-choose-amount-container">
                  <svg
                    translate="no"
                    class="ticket-choose-button"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                    @click="decreaseTicketsAmount(orderItem)"
                  >
                    <path
                      :class="{
                        'active-button':
                          order.filter(orderTicket => orderTicket.id === orderItem.id).length > 0,
                      }"
                      translate="no"
                      d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17317C0.00433284 8.00043 -0.193701 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C20 8.68678 19.7413 7.38642 19.2388 6.17317C18.7363 4.95991 17.9997 3.85752 17.0711 2.92893C16.1425 2.00035 15.0401 1.26375 13.8268 0.761205C12.6136 0.258658 11.3132 0 10 0ZM10 18C8.41775 18 6.87104 17.5308 5.55544 16.6518C4.23985 15.7727 3.21447 14.5233 2.60897 13.0615C2.00347 11.5997 1.84504 9.99113 2.15372 8.43928C2.4624 6.88743 3.22433 5.46197 4.34315 4.34315C5.46197 3.22433 6.88743 2.4624 8.43928 2.15372C9.99113 1.84504 11.5997 2.00346 13.0615 2.60896C14.5233 3.21447 15.7727 4.23984 16.6518 5.55544C17.5308 6.87103 18 8.41775 18 10C18 12.1217 17.1572 14.1566 15.6569 15.6569C14.1566 17.1571 12.1217 18 10 18ZM14 9H6C5.73479 9 5.48043 9.10536 5.2929 9.29289C5.10536 9.48043 5 9.73478 5 10C5 10.2652 5.10536 10.5196 5.2929 10.7071C5.48043 10.8946 5.73479 11 6 11H14C14.2652 11 14.5196 10.8946 14.7071 10.7071C14.8946 10.5196 15 10.2652 15 10C15 9.73478 14.8946 9.48043 14.7071 9.29289C14.5196 9.10536 14.2652 9 14 9Z"
                      fill="#979797"
                    />
                  </svg>
                  <div class="ticket-chosen-amount-container" translate="no">
                    {{ order.filter(orderTicket => orderTicket.id === orderItem.id).length }}
                  </div>
                  <svg
                    class="ticket-choose-button"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                    @click="increaseTicketsAmount(orderItem)"
                  >
                    <path
                      :class="{
                        'active-button': checkIfSelectedOrderItemCanBeHigher(orderItem),
                      }"
                      d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17317C0.00433284 8.00043 -0.193701 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C20 8.68678 19.7413 7.38642 19.2388 6.17317C18.7363 4.95991 17.9997 3.85752 17.0711 2.92893C16.1425 2.00035 15.0401 1.26375 13.8268 0.761205C12.6136 0.258658 11.3132 0 10 0ZM10 18C8.41775 18 6.87104 17.5308 5.55544 16.6518C4.23985 15.7727 3.21447 14.5233 2.60897 13.0615C2.00347 11.5997 1.84504 9.99113 2.15372 8.43928C2.4624 6.88743 3.22433 5.46197 4.34315 4.34315C5.46197 3.22433 6.88743 2.4624 8.43928 2.15372C9.99113 1.84504 11.5997 2.00346 13.0615 2.60896C14.5233 3.21447 15.7727 4.23984 16.6518 5.55544C17.5308 6.87103 18 8.41775 18 10C18 12.1217 17.1572 14.1566 15.6569 15.6569C14.1566 17.1571 12.1217 18 10 18ZM14 9H11V6C11 5.73478 10.8946 5.48043 10.7071 5.29289C10.5196 5.10536 10.2652 5 10 5C9.73479 5 9.48043 5.10536 9.2929 5.29289C9.10536 5.48043 9 5.73478 9 6V9H6C5.73479 9 5.48043 9.10536 5.2929 9.29289C5.10536 9.48043 5 9.73478 5 10C5 10.2652 5.10536 10.5196 5.2929 10.7071C5.48043 10.8946 5.73479 11 6 11H9V14C9 14.2652 9.10536 14.5196 9.2929 14.7071C9.48043 14.8946 9.73479 15 10 15C10.2652 15 10.5196 14.8946 10.7071 14.7071C10.8946 14.5196 11 14.2652 11 14V11H14C14.2652 11 14.5196 10.8946 14.7071 10.7071C14.8946 10.5196 15 10.2652 15 10C15 9.73478 14.8946 9.48043 14.7071 9.29289C14.5196 9.10536 14.2652 9 14 9Z"
                      fill="#979797"
                    />
                  </svg>
                </div>
                <div class="ticket-available-container">
                  <span
                    translate="no"
                    class="ticket-available"
                    :class="{ 'not-available': checkIfSelectedOrderItemIsUnavailable(orderItem) }"
                    >{{ calculateRemainingTicketsNumber(orderItem) }}</span
                  >/{{ calculateTotalOrderItemTicketsNumber(orderItem) }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="isZkm && !event.venueId" id="zkm-svg-container">
          <p class="svg-description">Dostupnost ulaznica</p>
          <svg id="zkm-svg" />
        </div>
        <floor-plan
          v-if="event.venueId"
          :key="floorPlanKey"
          style="margin-top: 1rem"
          :event="event"
          :ticket-locations="ticketLocations"
          :online-sale="false"
          :sellable-order-item-ids="event.eventTemplate ? orderItems.map(oi => oi.id) : undefined"
          @addSelectedTickets="selectedSeat"
          @removeSelectedTickets="removeSelectedSeat"
          @requiredSeats="handleFinishOrderButtonState"
          @addDialogElementToOrder="addDialogElementToOrder"
          @removeSelectedTableTickets="removeSelectedTableTickets"
        />
      </div>
      <div class="order-container">
        <div class="order-summary">
          <div class="order-summary-title">Order summary</div>
          <div v-for="ticket of countDuplicate()" :key="ticket.id">
            <div class="order-item-summary-container">
              <div class="order-item-container">
                {{ ticket.count }} × {{ ticket.name }}
                <div v-if="Object.keys(ticketLocations).length">
                  <div v-for="location in getTicketLocation(ticket.id)" :key="location">
                    <p style="margin: 3px 0; font-size: 14px">{{ location }}</p>
                  </div>
                </div>
              </div>
              <div class="order-item-price-container">
                {{ (ticket.count * ticket.price).toFixed(2) }} {{ currency }}
              </div>
            </div>
          </div>
          <div v-if="useEventFeePerTicket && Object.keys(fixedFees).length">
            <div
              v-for="[key, value] in Object.entries(fixedFees)"
              :key="key"
              class="order-item-summary-container"
            >
              <span>{{ value.count }} x CoreEvent fee {{ key * 1.25 }}</span>
              <span>{{ round(parseFloat(key) * 1.25 * value.count, 2) }} EUR</span>
            </div>
          </div>
          <div class="order-vat-container">
            {{ order.length ? 'VAT is included.' : 'No tickets selected.' }}
          </div>
        </div>
        <div v-if="totalPrice > 0" class="order-summary-total-container">
          <div class="order-total-container">
            <div class="order-total-text-container">Total</div>
            <div class="order-total-price-container">
              {{ (Number(totalPrice) + getFixedFeePerTicketAmount).toFixed(2) }}
              {{ Number(totalPrice) + getFixedFeePerTicketAmount > 0 ? currency : '' }}
            </div>
          </div>
          <button
            :disabled="disableFinishOrderButton || purchaseLoading"
            :style="`background: ${disableFinishOrderButton ? '#979797' : '#0277bd'}`"
            class="order-summary-finish-order-button"
            type="button"
            @click="openModal"
          >
            <span>FINISH ORDER</span>
            <div v-if="purchaseLoading" class="spinner" />
          </button>
        </div>
      </div>
    </div>
    <simple-snackbar
      :open.sync="snackbar.open"
      :message-prop="snackbar.message"
      :color="snackbar.color"
    />
  </div>
</template>
<script>
import PaymentTypeModal from '@/components/ticket-seller/tickets/payment-type-modal'
import FloorPlan from '@/components/accreditations/register-stepper/floor-plan.vue'
import { GET_TICKET_SELLERS_EVENT } from './../../../graphql/event.graphql'
import { formatDate, landingURL } from './../../../utils'
import moment from 'moment'
import { round } from 'lodash'
import { PURCHASE_ORDER_TICKET_SELLER } from '../../../graphql/order.graphql'
import SimpleSnackbar from '../../shared/snackbars/simple-snackbar.vue'
import { OrganizationCustomTicketFee, OrganizationType, TicketType } from '@/types'
import SuccessfulPaymentModal from '@/components/ticket-seller/tickets/successful-payment-modal.vue'

export const frontendURL = process.env.VUE_APP_FRONTEND_URL

const initialSeatColors = Object.freeze({
  AVAILABLE: '#979797',
  SELECTED: '#4DAFBB',
  UNAVAILABLE: '#f5f5f5',
  UNAVAILABLE_BORDER: '#d9d9d9',
})

export default {
  components: {
    SuccessfulPaymentModal,
    PaymentTypeModal,
    FloorPlan,
    SimpleSnackbar,
  },
  props: {
    eventId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      event: {},
      orderItems: [],
      order: [],
      totalPrice: 0,
      currency: '',
      dateAndTime: {},
      location: {},
      paymentMethod: 'CASH',
      seatInfo: null,
      floorPlanKey: 1,
      ticketLocations: {},
      fixedFees: {},
      disableFinishOrderButton: false,
      isPaymentTypeModalOpened: false,
      useEventFeePerTicket: false,
      shoppingCart: [],
      selectedSeats: [],
      title: '',
      cover: '',
      formatDate,
      purchaseLoading: false,
      snackbar: {
        open: false,
        message: '',
        color: '',
      },
      landingURL,
      numOfRemainingTickets: 0,
      startingSeatId: 0,
      organizationType: OrganizationType.REGULAR,
      isSuccessfulPaymentModalOpen: false,
      successfulOrder: null,
    }
  },
  computed: {
    isZkm() {
      return (
        !!this.event.organization &&
        this.event.organization.id == process.env.VUE_APP_ZKM_ORGANIZATION_ID
      )
    },
    getFixedFeePerTicketAmount() {
      return this.useEventFeePerTicket
        ? Object.entries(this.fixedFees).reduce(
            (acc, [key, value]) => (acc += Number(key) * 1.25 * value.count),
            0,
          )
        : 0
    },
  },
  apollo: {
    getTicketSellersEvent: {
      query: GET_TICKET_SELLERS_EVENT,
      variables() {
        return {
          eventId: this.eventId,
        }
      },
      async result({ data, loading }) {
        if (!loading) {
          const {
            getTicketSellersEvent: event,
            getTicketSellerOrganization: { fees },
          } = data
          this.useEventFeePerTicket = fees.useEventFeePerTicket
          this.event = event
          this.orderItems = event.orderItems.sort((a, b) =>
            parseInt(a.id) > parseInt(b.id) ? 1 : -1,
          )
          this.dateAndTime = moment(event.from).locale('en')
          this.title = event.title
          this.location = event.location
          this.cover = event.cover
          this.currency = event.orderItems[0].currency
          this.organizationType = event.organization.type
          if (event.organization.type === OrganizationType.THEATER) {
            this.numOfRemainingTickets = event.numOfRemainingTickets
            this.startingSeatId = this.event.audienceCapacity - this.numOfRemainingTickets
          }
        }
      },
    },
  },
  watch: {
    order: {
      handler(val) {
        this.totalPrice = 0
        this.fixedFees = {}
        if (val.length !== 0) {
          for (let ticket of val) {
            this.updateFixedFees(Number(ticket.price))
            this.totalPrice += Number(ticket.price)
          }
          this.totalPrice = this.totalPrice.toFixed(2)
        }
      },
    },
    isZkm: {
      immediate: true,
      handler(val) {
        if (val && !this.event.venueId) {
          this.$nextTick(() => {
            this.drawSvg()
          })
        }
      },
    },
  },
  methods: {
    updateFixedFees(ticketPrice) {
      if (!this.useEventFeePerTicket) {
        return
      }
      const fixedFee = this.getFixedFeeForSelectedTicket(ticketPrice)
      if (!fixedFee) {
        return
      }
      const existingFee = this.fixedFees[fixedFee]
      if (existingFee) {
        existingFee.count += 1
      } else {
        this.fixedFees[fixedFee] = { count: 1 }
      }
    },
    getFixedFeeForSelectedTicket(ticketPrice) {
      let ticketFee =
        this.event.fixedFeePerTicket ||
        (this.event.organization && this.event.organization.fixedFeePerTicket) ||
        0
      const customOrganizationFees = OrganizationCustomTicketFee[Number(this.event.organization.id)]
      if (customOrganizationFees) {
        ticketFee = this.getTicketFee(ticketPrice, customOrganizationFees)
      }

      return ticketFee
    },
    getTicketFee(price, organizationFees) {
      let ticketFee = 0
      for (const key in organizationFees) {
        const ranges = organizationFees[key]
        if (price >= ranges.min && price <= ranges.max) {
          return (ticketFee = Number(key))
        }
      }

      return ticketFee
    },
    addDialogElementToOrder(ticketInfo) {
      const [ticket, quantity, type, location] = ticketInfo
      if (type === TicketType.TABLE) {
        for (let i = 0; i < quantity; i++) {
          this.selectedSeat([ticket, location])
        }
      } else {
        this.shoppingCart = this.shoppingCart.filter(t => t.id !== ticket.id)
        this.order = this.order.filter(t => t.id !== ticket.id)
        for (let i = 0; i < quantity; i++) {
          this.shoppingCart.push(ticket)
          this.order.push(ticket)
        }
      }
    },
    round,
    removeSelectedTableTickets(seatInfo) {
      const [ticket, seatLocation, seatHasSameColorAsCurrTicket, oldTicket, tableDetails] = seatInfo
      this.removeSelectedSeat([
        ticket,
        seatLocation,
        seatHasSameColorAsCurrTicket,
        ticket,
        tableDetails,
      ])
      for (let i = 1; i < tableDetails[seatLocation]; i++) {
        const orderTicketIndex = this.shoppingCart.findIndex(oi => oi.id === oldTicket.id)
        this.order.splice(orderTicketIndex, 1)
        const shoppingCartTicketIndex = this.shoppingCart.findIndex(oi => oi.id === oldTicket.id)
        this.shoppingCart.splice(shoppingCartTicketIndex, 1)
      }
    },
    handleFinishOrderButtonState(requiredSeats) {
      return (this.disableFinishOrderButton = !!requiredSeats.length)
    },
    getTicketLocation(ticketId) {
      const locations = []
      if (this.ticketLocations[ticketId]) {
        this.ticketLocations[ticketId].map(location => {
          locations.push(
            `${location['section']} / ${location['row']} / Seat-${location['seats'].join(',')}`,
          )
        })
      }

      return locations
    },
    removeSelectedSeat(seatInfo) {
      const [currTicket, seatLocation, seatHasSameColorAsCurrTicket, oldTicket] = seatInfo
      const [section, row, seat] = seatLocation.split('_')
      const existingLocation = this.ticketLocations[oldTicket.id].find(
        location => location['section'] === section && location['row'] === row,
      )
      existingLocation.seats.splice(existingLocation.seats.indexOf(seat.split('-')[1]), 1)
      !existingLocation.seats.length &&
        this.ticketLocations[oldTicket.id].splice(
          this.ticketLocations[oldTicket.id].indexOf(existingLocation),
          1,
        )
      this.selectedSeats = this.selectedSeats.filter(s => s !== seatLocation)
      const orderTicketIndex = this.shoppingCart.findIndex(oi => oi.id === oldTicket.id)
      this.order.splice(orderTicketIndex, 1)
      const shoppingCartTicketIndex = this.shoppingCart.findIndex(oi => oi.id === oldTicket.id)
      this.shoppingCart.splice(shoppingCartTicketIndex, 1)
      !this.ticketLocations[oldTicket.id].length && delete this.ticketLocations[oldTicket.id]
      if (this.event.eventTemplate && !seatHasSameColorAsCurrTicket) {
        this.selectedSeat([currTicket, seatLocation])
      }
    },
    selectedSeat(seatInfo) {
      const [ticket, seatLocation] = seatInfo
      !!seatLocation && this.insertNewSeat(ticket, seatLocation)
      this.shoppingCart.push(ticket)
      this.order.push(ticket)
      this.selectedSeats.push(seatLocation)
    },
    insertNewSeat(ticket, seatLocation) {
      const [section, row, seat] = seatLocation.split('_')
      if (!this.ticketLocations[ticket.id]) {
        this.ticketLocations[ticket.id] = [
          {
            section: section,
            row: row,
            seats: [seat.split('-')[1]],
          },
        ]

        return
      }
      const existingLocation = this.ticketLocations[ticket.id].find(
        location => location['section'] === section && location['row'] === row,
      )
      if (existingLocation) {
        existingLocation.seats = [...new Set([...existingLocation.seats, seat.split('-')[1]])]

        return
      }
      this.ticketLocations[ticket.id] = [
        ...this.ticketLocations[ticket.id],
        {
          section: section,
          row: row,
          seats: [seat.split('-')[1]],
        },
      ]
    },
    increaseTicketsAmount(ticket) {
      if (
        [OrganizationType.REGULAR, OrganizationType.SPORT_ORGANIZATION].includes(
          this.organizationType,
        ) &&
        ticket.quantity > 0
      ) {
        this.order.push(ticket)

        return (this.orderItems = this.orderItems.map(orderItem =>
          orderItem.id === ticket.id
            ? { ...orderItem, quantity: orderItem.quantity - 1 }
            : orderItem,
        ))
      }

      if (this.numOfRemainingTickets <= 0) {
        return
      }
      this.order.push(ticket)
      --this.numOfRemainingTickets
      this.isZkm &&
        this.changeSvgSeatColor(
          (this.startingSeatId + this.order.length).toString(),
          initialSeatColors.SELECTED,
        )
    },
    decreaseTicketsAmount(ticket) {
      if (this.order.filter(orderItem => orderItem.id === ticket.id).length > 0) {
        this.order.splice(
          this.order.findIndex(orderItem => orderItem.id === ticket.id),
          1,
        )
        if (
          [OrganizationType.REGULAR, OrganizationType.SPORT_ORGANIZATION].includes(
            this.organizationType,
          )
        ) {
          return (this.orderItems = this.orderItems.map(orderItem =>
            orderItem.id === ticket.id
              ? { ...orderItem, quantity: orderItem.quantity + 1 }
              : orderItem,
          ))
        }

        ++this.numOfRemainingTickets
        this.isZkm &&
          this.changeSvgSeatColor(
            (this.startingSeatId + this.order.length + 1).toString(),
            initialSeatColors.AVAILABLE,
          )
      }
    },
    countDuplicate() {
      return Object.values(
        this.order.reduce((r, e) => {
          let k = `${e.id}`
          if (!r[k]) r[k] = { ...e, count: 1 }
          else r[k].count += 1

          return r
        }, {}),
      )
    },
    goToPublicPage() {
      window.open(`${this.landingURL}/events/${this.event.slug}`)
    },

    closeModal() {
      this.isPaymentTypeModalOpened = false
    },
    openModal() {
      this.isPaymentTypeModalOpened = true
    },

    async handlePurchase(paymentType, buyerEmail) {
      this.closeModal()
      this.purchaseLoading = true
      const { data: result, errors } = await this.$apollo.mutate({
        mutation: PURCHASE_ORDER_TICKET_SELLER,
        variables: {
          orderItemIds: this.order.map(orderItem => orderItem.id),
          paymentType,
          buyerEmail,
          eventId: this.event.id,
          selectedSeats: this.selectedSeats,
          price: parseFloat(this.totalPrice),
        },
      })

      if (errors) {
        const [e] = errors
        const {
          error,
          extensions: { statusCode },
        } = e
        let errorMsg = 'Oops, something went wrong! Try again later, or contact the administration.'
        if (statusCode == 500) {
          errorMsg = 'Oops, your purchase failed! Try again later, or contact the administration.'
          this.floorPlanKey++
        }
        if (statusCode == 400)
          errorMsg = 'Purchase failed. Not enough tickets available to finish the purchase.'

        this.snackbar = {
          open: true,
          color: 'error',
          message: errorMsg,
        }
        this.purchaseLoading = false

        return
      }

      this.purchaseLoading = false
      this.successfulOrder = result.purchaseOrderTicketSeller
      this.isSuccessfulPaymentModalOpen = true
    },
    calculateRemainingTicketsNumber(orderItem) {
      if (this.organizationType === OrganizationType.THEATER) {
        return this.numOfRemainingTickets
      }

      return orderItem.quantity
    },
    calculateTotalOrderItemTicketsNumber(orderItem) {
      if (this.organizationType === OrganizationType.THEATER) {
        return this.event.audienceCapacity
      }

      return orderItem.initialQuantity
    },
    drawSvg() {
      const svgn = 'http://www.w3.org/2000/svg'
      const svg = document.getElementById('zkm-svg')
      let cy = 25
      let cx = 25
      let j = 0
      let height = 150
      const containerWidth = document.getElementById('zkm-svg').clientWidth - cx
      for (let i = 0; i <= this.event.audienceCapacity; i++) {
        j += 1
        25 * j > containerWidth && (j = 1) && (cy += 25) && (height += 45)
        const circle = document.createElementNS(svgn, 'circle')
        circle.setAttributeNS(null, 'id', i.toString())
        circle.setAttributeNS(null, 'cx', (cx * j).toString())
        circle.setAttributeNS(null, 'cy', cy.toString())
        circle.setAttributeNS(null, 'r', '10')
        circle.setAttributeNS(
          null,
          'fill',
          i <= this.event.audienceCapacity - this.numOfRemainingTickets
            ? initialSeatColors.UNAVAILABLE
            : initialSeatColors.AVAILABLE,
        )
        svg.appendChild(circle)
      }
      document.getElementById('zkm-svg-container').style.height = `${height}px`
    },
    changeSvgSeatColor(id, color) {
      document.getElementById(id).setAttributeNS(null, 'fill', color)
    },
    checkIfSelectedOrderItemCanBeHigher(orderItem) {
      if (
        [OrganizationType.REGULAR, OrganizationType.SPORT_ORGANIZATION].includes(
          this.organizationType,
        )
      ) {
        return orderItem.quantity > 0
      }

      return this.numOfRemainingTickets > 0
    },
    checkIfSelectedOrderItemIsUnavailable(orderItem) {
      if (
        [OrganizationType.REGULAR, OrganizationType.SPORT_ORGANIZATION].includes(
          this.organizationType,
        )
      ) {
        return orderItem.quantity <= 0
      }

      return this.numOfRemainingTickets <= 0
    },
    onViewOrderClick() {
      if (!this.successfulOrder) {
        this.goBack()

        return
      }
      this.goTo(this.routes.ticketSeller.orderDetails, { orderId: this.successfulOrder.id })
    },
    onPrintTicketsClick() {
      if (!this.successfulOrder) {
        this.goBack()

        return
      }
      const url = `${frontendURL}/ticket-seller/print/${this.successfulOrder.uuid}`
      window.open(url, '_blank')
      this.goBack()
    },
    onSuccessfulPaymentModalClose() {
      this.isSuccessfulPaymentModalOpen = false
      this.goBack()
    },
  },
}
</script>

<style scoped>
.back-button-container {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  cursor: pointer;
  width: fit-content;
}

@media only screen and (max-width: 800px) {
  .back-button-container {
    margin-top: 0.5rem;
    margin-bottom: 0;
  }
}

.back-button-icon-container {
  margin-right: 1rem;
  display: flex;
  align-items: center;
}

.back-button-icon {
  height: 1rem;
}

.back-button-text-container {
  font-family: 'Raleway', sans-serif;
  font-size: 1.25rem;
  font-weight: bold;
  color: #ff4c00;
  line-height: 120%;
  letter-spacing: -0.015em;
}

.main-container {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(25%, 676px) auto;
}

.event-container {
  max-width: 676px;
  grid-column: 1/2;
  height: fit-content;
  border-radius: 0.5rem;
  background-color: #f5f5f5;
}

.order-container {
  max-width: 350px;
  min-width: 350px;
  min-height: 293px;
  grid-column: 2/3;
  background-color: #f5f5f5;
  border-radius: 2rem;
  overflow: hidden;
  margin-left: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

@media only screen and (max-width: 1455px) {
  .order-container {
    border-radius: 2rem 0 0 2rem;
  }
}

@media only screen and (max-width: 1200px) {
  .main-container {
    grid-template-columns: 1fr;
    grid-template-rows: min-content min-content;
    margin-bottom: 3rem;
    margin-top: 2rem;
    padding-right: 1.875rem;
  }

  .event-container {
    grid-column: 1;
    grid-row: 1;
    margin-bottom: 2rem;
  }

  .order-container {
    grid-column: 1;
    grid-row: 2;
    max-width: 676px;
    margin-left: 0;
    min-width: inherit;
    border-radius: 2rem;
  }
}

@media only screen and (max-width: 800px) {
  .main-container {
    padding-right: 0;
  }
}

.event-details-container {
  padding: 1.5rem 3rem;
}

.event-details-title {
  font-family: 'Raleway', sans-serif;
  font-weight: 800;
  font-size: 2.25rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  color: #0277bd;
  margin-bottom: 0.5rem;
}

.event-details-date-time-location {
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 1rem;
  line-height: 150%;
  color: #666666;
  margin-bottom: 0.5rem;
}

.public-page-link-container {
  display: flex;
  width: fit-content;
  align-items: center;
  cursor: pointer;
}

.public-page-link {
  font-family: 'Raleway', sans-serif;
  font-weight: bold;
  font-size: 1rem;
  line-height: 120%;
  letter-spacing: -0.015em;
  color: #0277bd;
  margin-right: 0.25rem;
}

.public-page-link-icon-container {
  display: flex;
}

.ticket-container {
  padding: 1rem 3rem;
  border-bottom: 1px solid #979797;
}

.ticket-details-container {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.ticket-info-container {
  display: flex;
  flex-direction: column;
}

.ticket-title {
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  font-size: 1.25rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  color: #0277bd;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-right: 1rem;
}

.ticket-description {
  margin-top: 0.5rem;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 0.875rem;
  line-height: 120%;
  color: #979797;
}

.ticket-price {
  font-family: 'Raleway', sans-serif;
  font-weight: 800;
  font-size: 1.25rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}

.ticket-purchase-container {
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.ticket-choose-amount-container {
  display: flex;
  align-items: center;
}

.ticket-chosen-amount-container {
  font-family: 'Raleway', sans-serif;
  font-weight: 800;
  font-size: 1.25rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #333333;
  min-width: 2rem;
  text-align: center;
}

.ticket-choose-button {
  width: 1.25rem;
  cursor: pointer;
}

.active-button {
  fill: #0277bd;
}

.ticket-available-container {
  font-family: 'Raleway', sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #666666;
}

.ticket-available {
  font-family: 'Raleway', sans-serif;
  font-weight: 800;
  font-size: 2rem;
  line-height: 120%;
  letter-spacing: -0.015em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
}

.not-available {
  color: #db384c;
}

.order-summary {
  padding: 1.5rem 2rem;
}

.order-summary-title {
  font-family: 'Raleway', sans-serif;
  font-weight: 600;
  font-size: 1.5rem;
  line-height: 120%;
  letter-spacing: -0.03em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #0277bd;
  margin-bottom: 0.5rem;
}

.order-item-summary-container {
  padding: 1rem 0;
  display: flex;
  width: 100%;
  justify-content: space-between;
  border-bottom: 1px solid #979797;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 1.125rem;
  line-height: 150%;
  color: #666666;
}

.order-vat-container {
  margin-top: 1rem;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 1rem;
  line-height: 150%;
  color: #979797;
}

.order-total-container {
  padding: 0px 2rem;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 1rem;
}

.order-total-text-container {
  font-family: 'Raleway', sans-serif;
  font-weight: bold;
  font-size: 1.25rem;
  line-height: 120%;
  letter-spacing: -0.015em;
  color: #333333;
}

.order-total-price-container {
  font-family: 'Raleway', sans-serif;
  font-weight: 800;
  font-size: 2rem;
  line-height: 120%;
  letter-spacing: -0.015em;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #333333;
}

.order-summary-finish-order-button {
  width: 100%;
  box-sizing: border-box;
  padding: 1rem 0;
  font-family: 'Raleway', sans-serif;
  font-weight: bold;
  font-size: 1.125rem;
  line-height: 120%;
  letter-spacing: -0.015em;
  color: #ffffff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  margin-left: 0.5rem;
  min-width: 1rem;
  width: 1rem;
  min-height: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  -webkit-animation: spin 1s linear infinite;
  align-self: center;
}

@keyframes spin {
  to {
    -webkit-transform: rotate(360deg);
  }
}

@-webkit-keyframes spin {
  to {
    -webkit-transform: rotate(360deg);
  }
}

@media only screen and (max-width: 425px) {
  .event-details-container {
    padding: 0.75rem 1.5rem;
  }

  .ticket-container {
    padding: 0.75rem 1.5rem;
  }
}

.purchase-page-container {
  width: fit-content;
  position: relative;
}

.v-snack {
  position: absolute !important;
  margin-right: 24px;
  transform: translateY(72px);
}

.svg-description {
  margin: 1.5rem 0 0 1rem;
  color: #333;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Rubik;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
}

#zkm-svg {
  width: 100%;
  height: 100%;
}
</style>
